import React, { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import ButtonsContainer from "./ButtonsContainer";
import ScoreCardModal from "../ScoreCardModal";
import Scores from "./Scores";
import Stats from "./Stats/Stats";
import ScoresFilterModal from "./ScoresFilterModal";
import { FilterState, USGACourse, USGATee } from "./ScoresFilterModal/utils";
import StatsFilterModal from "./StatsFilterModal";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import UserModel from "app/models/UserModel";

interface Props {
  isModalOpen: boolean;
  athleteData: Partial<UserModel>;
}

const ScoresAndStats = ({ isModalOpen, athleteData }: Props) => {
  const { t } = useTranslation();
  const USGA_OPTIONS = [
    { value: "scores", label: t("Scores") },
    { value: "stats", label: t("Stats") },
  ];
  const [buttonType, setButtonType] = useState(USGA_OPTIONS[0]);

  const [selectedScore, setSelectedScore] = useState(null);
  const [isScoreModalOpen, setIsScoreModalOpen] = useState(false);
  const [isScoresFilterModalOpen, setIsScoresFilterModalOpen] =
    useState<boolean>(false);
  const [isStatsFilterModalOpen, setIsStatsFilterModalOpen] =
    useState<boolean>(false);
  const [filters, setFilters] = useState<FilterState>({
    attendance: null,
    course: null,
    tee: null,
  });
  const [statsFilters, setStatsFilters] = useState<FilterState>({
    option: null,
    course: null,
    tee: null,
  });
  const [coursesAndTees, setCoursesAndTees] = useState<USGACourse[]>([]);
  const [tees, setTees] = useState<USGATee[]>([]);
  const [athlete, setAthlete] = useState(null);
  const athleteId = athleteData.athleteId || athleteData.externalId;

  const openScoreModal = (score) => {
    setSelectedScore(score);
    setIsScoreModalOpen(true);
  };

  const closeScoreModal = () => {
    setIsScoreModalOpen(false);
    setSelectedScore(null);
  };

  const scoresFilterModalHandler = () => {
    setIsScoresFilterModalOpen((prev) => !prev);
  };

  const statsFilterModalHandler = () => {
    setIsStatsFilterModalOpen((prev) => !prev);
  };

  const onFilterClick = () => {
    switch (buttonType.value) {
      case "scores":
        return scoresFilterModalHandler();
      case "stats":
        return statsFilterModalHandler();
    }
  };

  const fetchCoursesAndTees = async () => {
    try {
      const response = await schedulingFetchGet(
        `/student_profiles/${athleteId}/scores/recent_courses`,
      );
      setCoursesAndTees([
        {
          courseId: null,
          courseName: t("All Courses"),
          tees: [],
        },
        ...response,
      ]);
      setTees([
        {
          teeName: "All Tees",
          teeSetId: null,
        },
        ...response[0].tees,
      ]);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCoursesAndTees();
  }, []);

  const fetchAthleteProfile = () => {
    schedulingFetchGet(`/student_profiles/${athleteId}`)
      .then((response) => {
        setAthlete(response);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    fetchAthleteProfile();
  }, []);

  return (
    <>
      <div className={styles.scores_header}>
        <ButtonsContainer
          items={USGA_OPTIONS}
          containerStyle={styles.option_button_group}
          itemSelected={buttonType}
          setItemSelected={setButtonType}
        />
        <div className={styles.filters} onClick={onFilterClick}>
          <i className="ico ico-funnel" />
        </div>
        <ScoresFilterModal
          isModalOpen={isScoresFilterModalOpen}
          modalHandler={scoresFilterModalHandler}
          setFilters={setFilters}
          coursesAndTees={coursesAndTees}
          tees={tees}
        />
        <StatsFilterModal
          isModalOpen={isStatsFilterModalOpen}
          modalHandler={statsFilterModalHandler}
          setFilters={setStatsFilters}
          coursesAndTees={coursesAndTees}
          tees={tees}
        />
      </div>

      {buttonType.value === "scores" && (
        <Scores
          isModalOpen={isModalOpen}
          athleteId={athleteId}
          openScoreModal={openScoreModal}
          filters={filters}
          athlete={athlete}
        />
      )}

      {buttonType.value === "stats" && (
        <Stats
          isModalOpen={isModalOpen}
          athlete={athlete}
          filters={statsFilters}
        />
      )}

      {selectedScore && (
        <ScoreCardModal
          isModalOpen={isScoreModalOpen}
          modalHandler={closeScoreModal}
          score={selectedScore}
        />
      )}
    </>
  );
};

export default ScoresAndStats;
